// src/components/MessageArea.js
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { db, storage } from '../firebaseConfig';
import { collection, query, orderBy, getDocs, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import Avatar from './Avatar/Avatar';

const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
const ALLOWED_ATTACHMENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
];

const MessageArea = ({ conversation }) => {
  const { t } = useTranslation('common');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [userDetails, setUserDetails] = useState({});
  const [uploading, setUploading] = useState(false);
  const [attachError, setAttachError] = useState("");
  const fileInputRef = useRef(null);
  const auth = getAuth();
  const currentUser = auth.currentUser;

  useEffect(() => {
    const fetchMessages = async () => {
      const messagesRef = collection(db, "conversations", conversation.id, "messages");
      const q = query(messagesRef, orderBy("createdAt", "asc"));
      const querySnapshot = await getDocs(q);
      let fetchedMessages = [];
      let userIds = new Set();

      querySnapshot.forEach((docSnapshot) => {
        fetchedMessages.push(docSnapshot.data());
        userIds.add(docSnapshot.data().sender);
      });

      userIds.delete(currentUser.uid);  // Remove current user's ID

      const userDetails = {};
      for (let userId of userIds) {
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);
        userDetails[userId] = userSnap.exists() ? userSnap.data() : { avatar: null, firstName: 'Unknown' };
      }

      // Fetch current user details
      const currentUserRef = doc(db, 'users', currentUser.uid);
      const currentUserSnap = await getDoc(currentUserRef);
      if (currentUserSnap.exists()) {
        userDetails[currentUser.uid] = currentUserSnap.data();
      }

      setUserDetails(userDetails);
      setMessages(fetchedMessages);
    };

    if (currentUser) {
      fetchMessages();
    }
  }, [currentUser, conversation.id]);

  const handleSendMessage = async () => {
    if (newMessage.trim() !== "") {
      const messageRef = collection(db, "conversations", conversation.id, "messages");
      await addDoc(messageRef, {
        text: newMessage,
        sender: currentUser.uid,
        createdAt: serverTimestamp()
      });

      setMessages(prevMessages => [...prevMessages, {
        text: newMessage,
        sender: currentUser.uid,
        createdAt: null  // This is for immediate feedback, timestamp will be null until refreshed
      }]);
      setNewMessage("");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  const handleAttachmentClick = () => {
    if (uploading) return;
    fileInputRef.current?.click();
  };

  const handleAttachmentChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // permite reenviar o mesmo arquivo
    if (!file) return;

    setAttachError("");
    if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
      setAttachError(t('messages.attachInvalidType', 'Formato não suportado. Envie imagem, PDF, Word, Excel ou texto.'));
      return;
    }
    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      setAttachError(t('messages.attachTooLarge', 'O arquivo deve ter no máximo 10 MB.'));
      return;
    }

    setUploading(true);
    try {
      const safeName = file.name.replace(/[^\w.-]+/g, '_');
      const storageRef = ref(storage, `chat/${conversation.id}/${Date.now()}-${safeName}`);
      await uploadBytes(storageRef, file, { contentType: file.type });
      const url = await getDownloadURL(storageRef);

      const messagePayload = {
        text: '',
        attachmentUrl: url,
        attachmentName: file.name,
        attachmentType: file.type,
        sender: currentUser.uid,
        createdAt: serverTimestamp(),
      };
      const messageRef = collection(db, "conversations", conversation.id, "messages");
      await addDoc(messageRef, messagePayload);

      setMessages(prevMessages => [...prevMessages, { ...messagePayload, createdAt: null }]);
    } catch (err) {
      console.error('Erro ao enviar anexo:', err);
      setAttachError(t('messages.attachError', 'Erro ao enviar o anexo. Tente novamente.'));
    } finally {
      setUploading(false);
    }
  };

  const renderMessageBody = (message) => (
    <>
      {message.attachmentUrl && (
        message.attachmentType && message.attachmentType.startsWith('image/') ? (
          <a href={message.attachmentUrl} target="_blank" rel="noopener noreferrer">
            <img
              src={message.attachmentUrl}
              alt={message.attachmentName || 'Anexo'}
              className="chat-attachment-img"
            />
          </a>
        ) : (
          <a
            href={message.attachmentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="chat-attachment-file"
          >
            <span className="chat-attachment-icon" aria-hidden="true">📎</span>
            {message.attachmentName || t('messages.attachment', 'Anexo')}
          </a>
        )
      )}
      {message.text && <p>{message.text}</p>}
    </>
  );

  return (
    <main className="chat-area pb-5 md:pb-0">
      <div className="messages-container">
        {messages.map((message, index) => {
          const isCurrentUser = message.sender === currentUser.uid;
          const userDetail = userDetails[message.sender] || {};

          return (
            <div key={index} className={`message-wrapper ${isCurrentUser ? 'right' : 'left'}`}>
              {!isCurrentUser && (
                <Avatar
                  src={userDetail.avatar}
                  alt={`${userDetail.firstName || 'User'}'s Avatar`}
                  name={userDetail.firstName}
                />
              )}
              <div className={`chat-message ${isCurrentUser ? 'right' : 'left'}`}>
                {renderMessageBody(message)}
              </div>
              {isCurrentUser && (
                <Avatar
                  src={userDetails[currentUser.uid]?.avatar}
                  alt="Your Avatar"
                  name={userDetails[currentUser.uid]?.firstName || currentUser.displayName}
                />
              )}
            </div>
          );
        })}
      </div>
      {attachError && (
        <p className="chat-attach-error">{attachError}</p>
      )}
      <div className="message-input">
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_ATTACHMENT_TYPES.join(',')}
          onChange={handleAttachmentChange}
          style={{ display: 'none' }}
        />
        <button
          type="button"
          className="attach-button"
          onClick={handleAttachmentClick}
          disabled={uploading}
          title={t('messages.attach', 'Anexar arquivo')}
        >
          {uploading ? '…' : '📎'}
        </button>
        <input
          type="text"
          placeholder={t('messages.placeholder', 'Digite sua mensagem aqui...')}
          className="w-full p-0 h-10 border border-gray-300 rounded-xl"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={handleKeyPress}
        />
        <button onClick={handleSendMessage}>&#8594;</button> {/* Arrow symbol as button text */}
      </div>
    </main>
  );
};

export default MessageArea;

